import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Users, Dumbbell, ClipboardList, MessageSquare, Activity,
  Building2, UserCog, ScrollText, LogOut, Bell, Globe, Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "../auth";
import { useI18n, LANGS } from "../i18n";
import { LOGO, cx, Badge } from "./ui";
import api from "../api";

const NAV = [
  { to: "/app", key: "dashboard", icon: LayoutDashboard, roles: ["superadmin", "clinic_admin", "professional", "assistant"], end: true },
  { to: "/app/patients", key: "patients", icon: Users, roles: ["superadmin", "clinic_admin", "professional", "assistant"] },
  { to: "/app/exercices", key: "exercises", icon: Dumbbell, roles: ["superadmin", "clinic_admin", "professional"] },
  { to: "/app/programmes", key: "programs", icon: ClipboardList, roles: ["superadmin", "clinic_admin", "professional"] },
  { to: "/app/messagerie", key: "messages", icon: MessageSquare, roles: ["superadmin", "clinic_admin", "professional", "assistant"] },
  { to: "/app/hilldt", key: "hilldt", icon: Activity, roles: ["superadmin", "clinic_admin", "professional"] },
  { to: "/app/cabinets", key: "clinics", icon: Building2, roles: ["superadmin"] },
  { to: "/app/utilisateurs", key: "users", icon: UserCog, roles: ["superadmin", "clinic_admin"] },
  { to: "/app/audit", key: "audit", icon: ScrollText, roles: ["superadmin", "clinic_admin"] },
];

function LangSwitcher() {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="lang-switcher" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-navy px-2 py-1.5 rounded-lg hover:bg-slate-100">
        <Globe size={17} /> <span className="uppercase font-semibold">{lang}</span> <ChevronDown size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute end-0 mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-card z-20 py-1">
            {LANGS.map((l) => (
              <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => { setLang(l.code); setOpen(false); }}
                className={cx("w-full text-start px-4 py-2 text-sm hover:bg-vlg", lang === l.code ? "text-teal font-semibold" : "text-slate-600")}>
                {l.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NotifBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  const unread = items.filter((n) => !n.read).length;
  const markAll = () => api.post("/notifications/read-all").then(load);
  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={() => setOpen(!open)} className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600">
        <Bell size={19} />
        {unread > 0 && <span className="absolute top-1 end-1 h-4 w-4 rounded-full bg-alert text-white text-[10px] flex items-center justify-center">{unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute end-0 mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-card z-20 max-h-96 overflow-y-auto spina-scroll">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
              <span className="text-sm font-semibold text-navy">Notifications</span>
              <button onClick={markAll} className="text-xs text-teal hover:underline">Tout lire</button>
            </div>
            {items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">Aucune notification</div>}
            {items.map((n) => (
              <div key={n.id} className={cx("px-4 py-3 border-b border-slate-50 text-sm", !n.read && "bg-teal-50/40")}>
                <div className="font-medium text-navy">{n.title}</div>
                <div className="text-slate-500 text-xs mt-0.5">{n.body}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-vlg flex">
      {mobileOpen && <div className="fixed inset-0 bg-navy/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={cx("fixed lg:sticky top-0 z-40 h-screen w-64 bg-navy text-white flex flex-col transition-transform lg:translate-x-0",
        mobileOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full lg:!translate-x-0")}>
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <img src={LOGO} alt="SpinaGo" className="h-9 w-9 rounded-lg bg-white p-0.5 object-contain" />
          <div>
            <div className="font-head font-bold text-lg leading-none">SpinaGo</div>
            <div className="text-[10px] text-teal-400 tracking-wide">by SpinaTech</div>
          </div>
          <button className="ms-auto lg:hidden text-white/70" onClick={() => setMobileOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto spina-scroll py-4 px-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.key}`} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => cx("flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition",
                isActive ? "bg-teal text-white" : "text-white/70 hover:bg-white/10 hover:text-white")}>
              <n.icon size={18} /> {t(n.key)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-1">
            <div className="text-sm font-medium truncate">{user.name}</div>
            <div className="text-[11px] text-teal-400">{t(`role_${user.role}`)}</div>
            {user.clinic?.name && <div className="text-[11px] text-white/50 truncate mt-0.5">{user.clinic.name}</div>}
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-2 w-full px-3.5 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10">
            <LogOut size={18} /> {t("logout")}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen(true)} data-testid="mobile-menu"><Menu size={22} /></button>
          <div className="ms-auto flex items-center gap-1">
            <LangSwitcher />
            <NotifBell />
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
